import { useState, useEffect } from "react";
import { InputComp, Button, Icon } from "../../components";
import { signIn, resetPasswordManually, resetPassword } from "../../utils";
import { usePersistData } from "../../store";
import { useNavigate } from "react-router-dom";

const LoginForm = () => {
  const { setUser } = usePersistData();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [resetEmail, setResetEmail] = useState(""); // separate state for email during reset
  const [error, setError] = useState("");
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [isPasswordSet, setIsPasswordSet] = useState(false);
  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (isResetPassword) {
      setFormData({
        ...formData,
        password: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [isResetPassword]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
      setIsLoginSuccess(true);
    }
  }, []);

  // ✅ Auto-clear error messages after 3s
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSignIn = async () => {
    const { email, password } = formData;

    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }

    try {
      const user = await signIn(email, password);

      if (user) {
        setUser(user);
        localStorage.setItem("user", JSON.stringify(user));

        if (user.isFirstLogin) {
          setIsFirstLogin(true);
        } else {
          setIsFirstLogin(false);
          setIsPasswordSet(true);
          setIsLoginSuccess(true);
        }
      } else {
        setError("Login failed. Please check your credentials.");
      }
    } catch (err) {
      console.error("Sign-in error:", err);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const handleSetNewPassword = async () => {
    const { newPassword, confirmPassword } = formData;

    if (!newPassword || !confirmPassword) {
      setError("Both New Password and Confirm Password are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      await resetPasswordManually(formData.password, newPassword);
      setIsPasswordSet(true);
      setIsFirstLogin(false);
      setIsLoginSuccess(true);
      setError("");
    } catch (error) {
      console.error("Error resetting password:", error);
      setError(
        error.message || "An error occurred while resetting the password."
      );
    }
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetEmailChange = (inputKey, inputValue) => {
    setResetEmail(inputValue);
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setError("Please enter your email address.");
      return;
    }

    try {
      const success = await resetPassword(resetEmail);
      setResetEmail("");

      if (success) {
        setError("");
      } else {
        setError("Failed to send reset password email. Please try again.");
      }
    } catch (error) {
      console.error("Error requesting password reset:", error);
      setError("An error occurred while sending reset password email.");
    }
  };

  return (
    <div className="items-center justify-center flex">
      <div className="flex flex-col space-y-8 items-center">
        <Icon />
        <div className="flex flex-col space-y-3 text-center">
          <span className="font-semibold text-4xl">
            {isLoginSuccess
              ? "Welcome Back"
              : isFirstLogin
              ? "Set New Password"
              : isResetPassword
              ? "Forgot Password"
              : "Welcome Back"}
          </span>
          <span className="font-normal text-base">
            {isLoginSuccess
              ? "You are successfully logged in!"
              : isFirstLogin
              ? "You need to set a new password before proceeding."
              : isResetPassword
              ? "Enter your email to reset your password."
              : "Enter your credentials to log in."}
          </span>
        </div>

        <div className="w-full space-y-6">
          {isResetPassword ? (
            <>
              <InputComp
                label={"Email"}
                placeHolder={"Enter your email"}
                value={resetEmail}
                handleChange={handleResetEmailChange}
                type="text"
              />
              {error && <p className="text-red-500">{error}</p>}
              <Button
                onClick={handleForgotPassword}
                classes="!w-full justify-center"
              >
                Send Reset Link
              </Button>
              <p
                className="text-sm cursor-pointer text-primary mt-4"
                onClick={() => setIsResetPassword(false)}
              >
                Back to Login
              </p>
            </>
          ) : isFirstLogin ? (
            <>
              <InputComp
                label={"New Password"}
                placeHolder={"Enter your new password"}
                value={formData.newPassword}
                handleChange={handleInputChange}
                type="password"
                inputKey="newPassword"
              />
              <InputComp
                label={"Confirm Password"}
                placeHolder={"Confirm your new password"}
                value={formData.confirmPassword}
                handleChange={handleInputChange}
                type="password"
                inputKey="confirmPassword"
              />
              {error && <p className="text-red-500">{error}</p>}
              <Button
                onClick={handleSetNewPassword}
                classes="!w-full justify-center"
              >
                Set New Password
              </Button>
            </>
          ) : isLoginSuccess ? (
            navigate("/")
          ) : (
            <>
              <InputComp
                label={"Email"}
                placeHolder={"Enter your email"}
                value={formData.email}
                handleChange={handleInputChange}
                type="text"
                inputKey="email"
              />
              <InputComp
                label={"Password"}
                placeHolder={"Enter your password"}
                value={formData.password}
                handleChange={handleInputChange}
                type="password"
                inputKey="password"
              />
              <p
                className="text-sm cursor-pointer text-primary mt-4 justify-end items-end flex "
                onClick={() => setIsResetPassword(true)}
              >
                Forgot Password?
              </p>
              {error && <p className="text-red-500">{error}</p>}
              <Button onClick={handleSignIn} classes="!w-full justify-center">
                Sign in
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
